/* ================================================================
   HighlightOverlay — Renders saved highlights as <mark> wrappers
   on assistant message text. Clicking a highlight shows its note.
   Right-click shows a remove option.
   ================================================================ */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Highlight, HighlightColor } from '../highlights';
import { getHighlightsForMessage, removeHighlight, HIGHLIGHT_COLORS } from '../highlights';
import { useToast } from '../contexts/ToastContext';

interface Props {
  messageId: string;
  /** Refresh counter — bumped by parent after highlight changes */
  refreshKey: number;
  /** Called when a highlight is removed so parent can refresh */
  onHighlightRemoved: () => void;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  highlight: Highlight | null;
}

const TOOLTIP_INITIAL: TooltipState = { visible: false, x: 0, y: 0, highlight: null };

/**
 * Walk all text nodes inside `root` and wrap regions that match saved
 * highlights with <mark> elements. Uses text-matching with offset hints.
 */
function applyHighlightsToDOM(
  root: Element,
  highlights: Highlight[],
  onMarkClick: (h: Highlight, rect: DOMRect) => void,
  onMarkContextMenu: (e: React.MouseEvent | MouseEvent, h: Highlight, rect: DOMRect) => void,
): (() => void) | null {
  if (highlights.length === 0) return null;

  const marks: HTMLElement[] = [];

  for (const hl of highlights) {
    // Strategy: find the exact text within the prose and wrap it
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node: Text | null;
    let charCount = 0;
    let found = false;

    while ((node = walker.nextNode() as Text | null)) {
      const nodeLen = node.length;

      // Check if this text node contains (or overlaps) the highlight range
      if (charCount + nodeLen > hl.startOffset && !found) {
        // Try text matching first (more reliable across re-renders)
        const fullText = root.textContent || '';
        const idx = fullText.indexOf(hl.text, Math.max(0, hl.startOffset - 20));
        if (idx === -1) break; // text changed, skip

        // Now find the text nodes that span this range
        wrapTextRange(root, idx, idx + hl.text.length, hl, marks, onMarkClick, onMarkContextMenu);
        found = true;
        break;
      }
      charCount += nodeLen;
    }

    // Fallback: if offset-based search failed, try pure text search
    if (!found) {
      const fullText = root.textContent || '';
      const idx = fullText.indexOf(hl.text);
      if (idx >= 0) {
        wrapTextRange(root, idx, idx + hl.text.length, hl, marks, onMarkClick, onMarkContextMenu);
      }
    }
  }

  // Return cleanup function
  return () => {
    for (const mark of marks) {
      const parent = mark.parentNode;
      if (!parent) continue;
      while (mark.firstChild) {
        parent.insertBefore(mark.firstChild, mark);
      }
      parent.removeChild(mark);
    }
    // Normalize to merge adjacent text nodes
    root.normalize();
  };
}

/**
 * Wrap a character range [start, end) across text nodes inside `root`
 * with <mark> elements.
 */
function wrapTextRange(
  root: Element,
  start: number,
  end: number,
  hl: Highlight,
  marks: HTMLElement[],
  onMarkClick: (h: Highlight, rect: DOMRect) => void,
  onMarkContextMenu: (e: React.MouseEvent | MouseEvent, h: Highlight, rect: DOMRect) => void,
): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Text | null;
  let charCount = 0;
  const nodesToWrap: { node: Text; from: number; to: number }[] = [];

  while ((node = walker.nextNode() as Text | null)) {
    const nodeStart = charCount;
    const nodeEnd = charCount + node.length;

    if (nodeEnd > start && nodeStart < end) {
      const from = Math.max(0, start - nodeStart);
      const to = Math.min(node.length, end - nodeStart);
      nodesToWrap.push({ node, from, to });
    }
    if (nodeEnd >= end) break;
    charCount += node.length;
  }

  for (const { node: textNode, from, to } of nodesToWrap) {
    // Skip if already wrapped
    if (textNode.parentElement?.tagName === 'MARK' &&
        textNode.parentElement.dataset.highlightId === hl.id) {
      continue;
    }

    const range = document.createRange();
    range.setStart(textNode, from);
    range.setEnd(textNode, to);

    const mark = document.createElement('mark');
    mark.className = `hl-mark hl-mark--${hl.color}`;
    mark.dataset.highlightId = hl.id;
    mark.style.backgroundColor = HIGHLIGHT_COLORS[hl.color as HighlightColor].bg;
    mark.style.borderBottom = `2px solid ${HIGHLIGHT_COLORS[hl.color as HighlightColor].border}`;
    mark.style.borderRadius = '2px';
    mark.style.cursor = 'pointer';
    if (hl.note) {
      mark.title = hl.note;
    }

    mark.addEventListener('click', (e) => {
      e.stopPropagation();
      const rect = mark.getBoundingClientRect();
      onMarkClick(hl, rect);
    });

    mark.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = mark.getBoundingClientRect();
      onMarkContextMenu(e, hl, rect);
    });

    try {
      range.surroundContents(mark);
      marks.push(mark);
    } catch {
      // surroundContents fails if range crosses element boundaries
      // Fallback: just highlight the text within this node
      const fragment = range.extractContents();
      mark.appendChild(fragment);
      range.insertNode(mark);
      marks.push(mark);
    }
  }
}

export default function HighlightOverlay({ messageId, refreshKey, onHighlightRemoved }: Props) {
  const containerRef = useRef<Element | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState>(TOOLTIP_INITIAL);
  const [contextMenu, setContextMenu] = useState<TooltipState>(TOOLTIP_INITIAL);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const ctxRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();

  const handleMarkClick = useCallback((h: Highlight, rect: DOMRect) => {
    setContextMenu(TOOLTIP_INITIAL);
    if (h.note) {
      setTooltip({
        visible: true,
        x: rect.left + rect.width / 2,
        y: rect.top - 4,
        highlight: h,
      });
    }
  }, []);

  const handleMarkContextMenu = useCallback((_e: React.MouseEvent | MouseEvent, h: Highlight, rect: DOMRect) => {
    setTooltip(TOOLTIP_INITIAL);
    setContextMenu({
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.bottom + 4,
      highlight: h,
    });
  }, []);

  const handleRemove = useCallback(() => {
    if (!contextMenu.highlight) return;
    removeHighlight(contextMenu.highlight.id);
    setContextMenu(TOOLTIP_INITIAL);
    onHighlightRemoved();
    addToast('info', 'Highlight removed', 1500);
  }, [contextMenu.highlight, onHighlightRemoved, addToast]);

  /* ── Apply/cleanup highlights on mount and refresh ── */
  useEffect(() => {
    // Find the message-prose element for this message
    const msgEl = document.querySelector(`[data-msg-id="${messageId}"]`);
    const proseEl = msgEl?.querySelector('.message-prose');
    if (!proseEl) return;
    containerRef.current = proseEl;

    // Cleanup previous marks
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    const highlights = getHighlightsForMessage(messageId);
    if (highlights.length === 0) return;

    // Small delay to ensure DOM is settled after React render
    const id = requestAnimationFrame(() => {
      cleanupRef.current = applyHighlightsToDOM(
        proseEl,
        highlights,
        handleMarkClick,
        handleMarkContextMenu,
      );
    });

    return () => {
      cancelAnimationFrame(id);
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [messageId, refreshKey, handleMarkClick, handleMarkContextMenu]);

  /* ── Click outside to dismiss tooltip/context menu ── */
  useEffect(() => {
    if (!tooltip.visible && !contextMenu.visible) return;
    const handleClick = (e: MouseEvent) => {
      if (tooltipRef.current?.contains(e.target as Node)) return;
      if (ctxRef.current?.contains(e.target as Node)) return;
      setTooltip(TOOLTIP_INITIAL);
      setContextMenu(TOOLTIP_INITIAL);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [tooltip.visible, contextMenu.visible]);

  return (
    <>
      {/* Note tooltip */}
      {tooltip.visible && tooltip.highlight?.note && (
        <div
          ref={tooltipRef}
          className="hl-tooltip"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
          }}
        >
          <div className="hl-tooltip-text">{tooltip.highlight.note}</div>
          <div className="hl-tooltip-meta">
            {new Date(tooltip.highlight.created_at).toLocaleDateString()}
          </div>
        </div>
      )}

      {/* Context menu (remove) */}
      {contextMenu.visible && contextMenu.highlight && (
        <div
          ref={ctxRef}
          className="hl-context-menu"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
        >
          <button className="hl-context-menu-btn" onClick={handleRemove}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Remove highlight
          </button>
          {contextMenu.highlight.note && (
            <div className="hl-context-menu-note">
              {contextMenu.highlight.note}
            </div>
          )}
        </div>
      )}
    </>
  );
}
